export type UrgencyBand = 'high' | 'medium' | 'low';

export interface ComparisonStep {
  pivotIndex: number;
  lowIndex: number;
  highIndex: number;
  choice: 'left' | 'right' | 'skip' | 'too-tough' | null;
}

export interface ComparisonSession {
  taskId: string;
  band: UrgencyBand;
  candidates: string[]; // task IDs sorted by urgencyRank
  lowIndex: number;
  highIndex: number;
  history: ComparisonStep[];
}

const BAND_RANGES: Record<UrgencyBand, [number, number]> = {
  high: [8.0, 10.0],
  medium: [4.0, 7.9],
  low: [0.0, 3.9],
};

export function getBandFromNumber(urgencyNumber: number): UrgencyBand {
  // Thresholds: High >= 8, Medium 4-7.9, Low < 4
  if (urgencyNumber >= 8) return 'high';
  if (urgencyNumber >= 4) return 'medium';
  return 'low';
}

export function getBandMidpoint(band: UrgencyBand): number {
  const [min, max] = BAND_RANGES[band];
  return (min + max) / 2;
}

export function getBandMax(band: UrgencyBand): number {
  return BAND_RANGES[band][1];
}

export function deriveUrgencyNumber(
  urgencyRank: number | null | undefined,
  band: UrgencyBand | null,
  totalInBand: number
): number {
  if (!band || urgencyRank == null || totalInBand === 0) {
    return band ? getBandMidpoint(band) : 5;
  }

  const [min, max] = BAND_RANGES[band];

  if (totalInBand === 1) return max;

  const position = urgencyRank - 1; // 0-indexed
  return max - (position / (totalInBand - 1)) * (max - min);
}

export function createComparisonSession(
  taskId: string,
  band: UrgencyBand,
  candidateIds: string[]
): ComparisonSession {
  return {
    taskId,
    band,
    candidates: candidateIds,
    lowIndex: 0,
    highIndex: candidateIds.length - 1,
    history: [],
  };
}

export function getPivotIndex(session: ComparisonSession): number {
  return Math.floor((session.lowIndex + session.highIndex) / 2);
}

export function isSessionConverged(session: ComparisonSession): boolean {
  return session.lowIndex > session.highIndex;
}

export function getFinalRank(session: ComparisonSession): number {
  return session.lowIndex + 1;
}

export function processChoice(
  session: ComparisonSession,
  choice: 'left' | 'right' | 'skip' | 'too-tough'
): ComparisonSession {
  const pivotIndex = getPivotIndex(session);

  const step: ComparisonStep = {
    pivotIndex,
    lowIndex: session.lowIndex,
    highIndex: session.highIndex,
    choice,
  };

  const newHistory = [...session.history, step];

  switch (choice) {
    case 'left':
      // New task is higher priority than pivot
      return {
        ...session,
        highIndex: pivotIndex - 1,
        history: newHistory,
      };
    case 'right':
      // Pivot task is higher priority than new task
      return {
        ...session,
        lowIndex: pivotIndex + 1,
        history: newHistory,
      };
    case 'too-tough':
    case 'skip':
      // Both mean similar priority - insert at pivot position
      return {
        ...session,
        lowIndex: pivotIndex + 1,
        highIndex: pivotIndex,
        history: newHistory,
      };
    default:
      return session;
  }
}

export function undoLastChoice(session: ComparisonSession): ComparisonSession | null {
  if (session.history.length === 0) return null;

  const previousStep = session.history[session.history.length - 1];
  return {
    ...session,
    lowIndex: previousStep.lowIndex,
    highIndex: previousStep.highIndex,
    history: session.history.slice(0, -1),
  };
}

export function getEstimatedComparisonsRemaining(session: ComparisonSession): number {
  const range = session.highIndex - session.lowIndex + 1;
  if (range <= 0) return 0;
  return Math.ceil(Math.log2(range));
}
